import { z } from 'zod';
import { apiUrl } from '../config/api';
import { apiClient } from '../lib/apiClient';
import { envelopeSchema } from '../lib/schemas';

const costGroupSchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  name: z.string(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
});

const assignmentSchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  employee_id: z.string(),
  cost_group_id: z
    .union([z.string(), z.number()])
    .optional()
    .transform((v) => (v === undefined ? undefined : String(v))),
  cost_group: z.string(),
  calendar_year: z.number(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
});

const yearRateSchema = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  group_id: z.number().optional(),
  cost_group_id: z
    .union([z.string(), z.number()])
    .optional()
    .transform((v) => (v === undefined ? undefined : String(v))),
  cost_group: z.string(),
  calendar_year: z.number(),
  cost_rate: z.number(),
  sales_rate: z.number(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
});

export type CostGroup = z.infer<typeof costGroupSchema>;
export type EmployeeCostGroupAssignment = z.infer<typeof assignmentSchema>;
export type CostGroupYearRate = z.infer<typeof yearRateSchema>;

export const costGroupService = {
  getCostGroups: async (): Promise<CostGroup[]> => {
    const result = await apiClient.get(apiUrl.costGroups(), {
      validate: envelopeSchema('cost_groups', z.array(costGroupSchema)),
      context: 'getCostGroups',
    });
    return result.cost_groups ?? [];
  },

  getCostGroup: async (id: string): Promise<CostGroup> => {
    const result = await apiClient.get(apiUrl.costGroups(id), {
      validate: envelopeSchema('cost_group', costGroupSchema),
      context: 'getCostGroup',
    });
    return result.cost_group;
  },

  createCostGroup: async (payload: { name: string }): Promise<CostGroup> => {
    const result = await apiClient.post(apiUrl.costGroups(), payload, {
      validate: envelopeSchema('cost_group', costGroupSchema),
      context: 'createCostGroup',
    });
    return result.cost_group;
  },

  updateCostGroup: async (id: string, payload: { name: string }): Promise<CostGroup> => {
    const result = await apiClient.put(apiUrl.costGroups(id), payload, {
      validate: envelopeSchema('cost_group', costGroupSchema),
      context: 'updateCostGroup',
    });
    return result.cost_group;
  },

  deleteCostGroup: async (id: string): Promise<void> => {
    await apiClient.delete(apiUrl.costGroups(id), { context: 'deleteCostGroup' });
  },

  getAllAssignments: async (): Promise<EmployeeCostGroupAssignment[]> => {
    const result = await apiClient.get(apiUrl.costGroupAssignments(), {
      validate: envelopeSchema('assignments', z.array(assignmentSchema)),
      context: 'getAllAssignments',
    });
    return result.assignments ?? [];
  },

  getAssignmentsByEmployeeId: async (
    employeeId: string
  ): Promise<EmployeeCostGroupAssignment[]> => {
    const result = await apiClient.get(apiUrl.employeeCostGroupAssignments(employeeId), {
      validate: envelopeSchema('assignments', z.array(assignmentSchema)),
      context: 'getAssignmentsByEmployeeId',
    });
    return result.assignments ?? [];
  },

  createAssignment: async (
    employeeId: string,
    payload: { cost_group: string; calendar_year: number }
  ): Promise<EmployeeCostGroupAssignment> => {
    const result = await apiClient.post(apiUrl.employeeCostGroupAssignments(employeeId), payload, {
      validate: envelopeSchema('assignment', assignmentSchema),
      context: 'createAssignment',
    });
    return result.assignment;
  },

  updateAssignment: async (
    employeeId: string,
    aid: string,
    payload: { cost_group: string; calendar_year: number }
  ): Promise<EmployeeCostGroupAssignment> => {
    const result = await apiClient.put(
      apiUrl.employeeCostGroupAssignments(employeeId, aid),
      payload,
      { validate: envelopeSchema('assignment', assignmentSchema), context: 'updateAssignment' }
    );
    return result.assignment;
  },

  deleteAssignment: async (employeeId: string, aid: string): Promise<void> => {
    await apiClient.delete(apiUrl.employeeCostGroupAssignments(employeeId, aid), {
      context: 'deleteAssignment',
    });
  },

  getYearRatesByCostGroupId: async (groupId: string): Promise<CostGroupYearRate[]> => {
    const result = await apiClient.get(apiUrl.costGroupYearRates(groupId), {
      validate: envelopeSchema('year_rates', z.array(yearRateSchema)),
      context: 'getYearRatesByCostGroupId',
    });
    return result.year_rates ?? [];
  },

  createYearRate: async (
    groupId: string,
    payload: { calendar_year: number; cost_rate: number; sales_rate: number }
  ): Promise<CostGroupYearRate> => {
    const result = await apiClient.post(apiUrl.costGroupYearRates(groupId), payload, {
      validate: envelopeSchema('year_rate', yearRateSchema),
      context: 'createYearRate',
    });
    return result.year_rate;
  },

  updateYearRate: async (
    groupId: string,
    yid: string,
    payload: { calendar_year: number; cost_rate: number; sales_rate: number }
  ): Promise<CostGroupYearRate> => {
    const result = await apiClient.put(apiUrl.costGroupYearRates(groupId, yid), payload, {
      validate: envelopeSchema('year_rate', yearRateSchema),
      context: 'updateYearRate',
    });
    return result.year_rate;
  },

  deleteYearRate: async (groupId: string, yid: string): Promise<void> => {
    await apiClient.delete(apiUrl.costGroupYearRates(groupId, yid), {
      context: 'deleteYearRate',
    });
  },
};
